import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, resolveImageUrl } from '@/lib/axios';
import { Product, CartItem } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Clock, Check, MessageCircle, ShoppingBag, ArrowLeft, Image as ImageIcon, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { STORE_WHATSAPP_NUMBER } from '@/lib/constants';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [activeImage, setActiveImage] = useState<string>('');
  const [productVariants, setProductVariants] = useState<any[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const prodRes = await api.get<Product>(`/products/${slug}`);
      const prod = prodRes?.data && typeof prodRes.data === 'object' ? prodRes.data : null;
      setProduct(prod);

      if (prod) {
        const resolvedMain = resolveImageUrl(prod.mainImageUrl);
        setActiveImage(resolvedMain || '');
        let vars: any[] = [];
        try {
          vars = JSON.parse(prod.variants || '[]');
        } catch {
          vars = [];
        }
        vars = vars.map((v) => ({ ...v, imageUrl: v.imageUrl ? resolveImageUrl(v.imageUrl) : v.imageUrl }));
        setProductVariants(vars);
        if (vars.length > 0) {
          setSelectedVariant(vars[0]);
          if (vars[0].imageUrl) {
            setActiveImage(resolveImageUrl(vars[0].imageUrl));
          }
        }
      }
    } catch {
      setProduct(null);
      toast.error('No se pudo encontrar el producto');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectVariant = (v: any) => {
    setSelectedVariant(v);
    if (v.imageUrl) {
      setActiveImage(v.imageUrl);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    const currentCart: CartItem[] = JSON.parse(localStorage.getItem('mila_cart') || '[]');
    const basePrice = product.isOnSale && product.promotionalPrice ? product.promotionalPrice : product.salePrice;
    const priceToUse = basePrice + (selectedVariant?.priceAdjustment || 0);

    const descParts: string[] = [];
    if (selectedVariant?.name) descParts.push(`Variante: ${selectedVariant.name}`);

    const itemImage = selectedVariant?.imageUrl || activeImage || resolveImageUrl(product.mainImageUrl);

    const newItem: CartItem = {
      productId: product.id,
      productName: product.name,
      unitPrice: priceToUse,
      quantity: 1,
      variantName: selectedVariant?.name,
      variantPriceAdjustment: selectedVariant?.priceAdjustment || 0,
      notes: descParts.join(' • '),
      imageUrl: itemImage
    };

    // Si ya existe un producto con la misma variante, sumamos la cantidad
    const existingIndex = currentCart.findIndex(
      (item) =>
        item.productId === newItem.productId &&
        (item.variantName || '') === (newItem.variantName || '')
    );

    let updatedCart: CartItem[];
    if (existingIndex > -1) {
      updatedCart = [...currentCart];
      updatedCart[existingIndex].quantity += 1;
    } else {
      updatedCart = [...currentCart, newItem];
    }

    localStorage.setItem('mila_cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cart-updated'));

    toast.success('¡Agregado al carrito!', {
      description: `${product.name} ${selectedVariant ? `• ${selectedVariant.name}` : ''}`,
      action: {
        label: 'Ir al Carrito',
        onClick: () => navigate('/checkout')
      }
    });
  };

  if (isLoading) {
    return <div className="container mx-auto py-24 text-center text-muted-foreground">Cargando detalles del producto...</div>;
  }

  if (!product) {
    return (
      <div className="container mx-auto py-24 text-center space-y-4">
        <h2 className="text-2xl font-bold">Producto no encontrado</h2>
        <Button asChild variant="outline">
          <Link to="/tienda"><ArrowLeft className="mr-2 h-4 w-4" /> Volver a la Tienda</Link>
        </Button>
      </div>
    );
  }

  const basePrice = product.isOnSale && product.promotionalPrice ? product.promotionalPrice : product.salePrice;
  const activePrice = basePrice + (selectedVariant?.priceAdjustment || 0);

  return (
    <div className="container mx-auto py-8 md:py-16 px-4 max-w-6xl">
      <Link to="/tienda" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
        <ArrowLeft className="mr-2 h-4 w-4" /> Volver a la Tienda
      </Link>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        {/* Galería de imágenes */}
        <div className="space-y-4">
          <div className="aspect-square bg-card rounded-2xl border border-border/80 overflow-hidden relative shadow-sm flex items-center justify-center">
            {activeImage || product.mainImageUrl ? (
              <img src={activeImage || product.mainImageUrl} alt={product.name} className="w-full h-full object-cover transition-all duration-300" />
            ) : (
              <ImageIcon className="h-20 w-20 text-muted-foreground/30" />
            )}
            {product.isOnSale && (
              <Badge className="absolute top-4 left-4 bg-destructive text-destructive-foreground text-sm font-semibold shadow-md">
                OFERTA
              </Badge>
            )}
          </div>
        </div>

        {/* Info y Configuración */}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">{product.name}</h1>
            <div className="flex items-baseline gap-3 mt-3">
              <span className="text-3xl font-extrabold text-primary">
                ${activePrice.toLocaleString('es-AR')}
              </span>
              {product.isOnSale && product.promotionalPrice && (
                <span className="text-lg line-through text-muted-foreground">
                  ${product.salePrice.toLocaleString('es-AR')}
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-4 leading-relaxed text-sm md:text-base">
              {product.description || product.shortDescription || 'Pieza de diseño elaborada mediante fabricación aditiva de alta precisión.'}
            </p>
          </div>

          {/* Variaciones estilo MercadoLibre si están configuradas */}
          {productVariants.length > 0 && (
            <div className="space-y-3 pt-2 border-t border-border/60">
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold text-foreground">Opción / Variante:</span>
                <span className="text-primary font-medium">{selectedVariant?.name || 'Selecciona'}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {productVariants.map((v, idx) => {
                  const isSelected = selectedVariant?.name === v.name;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSelectVariant(v)}
                      className={`p-2.5 rounded-xl border text-left transition-all flex items-center justify-between text-xs ${
                        isSelected
                          ? 'border-primary bg-primary/5 text-foreground ring-1 ring-primary/40 font-semibold'
                          : 'border-border/70 hover:bg-muted/40 text-muted-foreground'
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate">
                        {v.imageUrl ? (
                          <img src={v.imageUrl} alt={v.name} className="w-6 h-6 rounded-md object-cover border" />
                        ) : v.colorHex ? (
                          <div className="w-4 h-4 rounded-full border shrink-0" style={{ backgroundColor: v.colorHex }} />
                        ) : null}
                        <span className="truncate">{v.name}</span>
                      </div>
                      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}



          {/* Entrega estimada & Stock */}
          {product.stockQuantity > 0 ? (
            <Card className="p-4 bg-emerald-500/10 border-emerald-500/30 flex items-center gap-3 text-xs md:text-sm">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800 dark:text-emerald-300">
                  ¡En stock para entrega inmediata! ({product.stockQuantity} {product.stockQuantity === 1 ? "unidad" : "unidades"})
                </p>
                <p className="text-muted-foreground">Despacho por correo o retiro en el taller en 24 a 48 hs hábiles.</p>
              </div>
            </Card>
          ) : (product.allowBackorder ?? true) ? (
            <Card className="p-4 bg-muted/30 border-border/60 flex items-center gap-3 text-xs md:text-sm">
              <Clock className="h-5 w-5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Fabricación bajo demanda</p>
                <p className="text-muted-foreground">Pieza fabricada en nuestro taller. Lista para despachar en aprox. {product.estimatedDeliveryDays || 5} días hábiles.</p>
              </div>
            </Card>
          ) : (
            <Card className="p-4 bg-destructive/10 border-destructive/30 flex items-center gap-3 text-xs md:text-sm">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
              <div>
                <p className="font-semibold text-destructive">Temporalmente Agotado</p>
                <p className="text-muted-foreground">Sin stock físico por el momento. Consultanos por WhatsApp para reservarlo.</p>
              </div>
            </Card>
          )}

          {/* Botones de acción */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            {product.stockQuantity === 0 && !(product.allowBackorder ?? true) ? (
              <Button size="lg" className="flex-1 text-base gap-2 bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" disabled>
                Sin Stock Disponible
              </Button>
            ) : (
              <Button size="lg" className="flex-1 text-base gap-2" onClick={handleAddToCart} asChild>
                <Link to="/checkout">
                  <ShoppingBag className="h-5 w-5" /> Comprar Ahora
                </Link>
              </Button>
            )}

            <Button size="lg" variant="outline" className="flex-1 text-base gap-2" asChild>
              <a
                href={`https://wa.me/${STORE_WHATSAPP_NUMBER}?text=${encodeURIComponent(`¡Hola Mila! Quería consultar por el producto ${product.name}${selectedVariant ? ` (${selectedVariant.name})` : ''}.`)}`}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle className="h-5 w-5 text-green-600" /> Consultar por WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
